import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, ExternalLink, AlertCircle, CheckCircle2 } from "lucide-react";
import { useTranslations } from "use-intl";
import { ErpService } from "@/services/erpService";
import { userFacingErrorMessage } from '@/utils/userFacingError';

interface ErpConnectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export function ErpConnectionModal({ isOpen, onClose, onSuccess }: ErpConnectionModalProps) {
  const t = useTranslations('consultantTax.erp.modal');
  const [authUrl, setAuthUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const erpService = new ErpService();

  // Initialize and get auth URL when modal opens
  useEffect(() => {
    if (isOpen && !authUrl) {
      initiateConnection();
    }
  }, [isOpen]);

  const initiateConnection = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await erpService.connectToErp();
      setAuthUrl(response.url);
    } catch (err) {
      console.error('Failed to initiate ERP connection:', err);
      setError(userFacingErrorMessage(err, t('failedConnect')));
    } finally {
      setIsLoading(false);
    }
  };

  const handleConnectRedirect = () => {
    if (authUrl) {
      // Redirect to Microsoft OAuth page
      window.location.href = authUrl;
    }
  };

  const handleClose = () => {
    setAuthUrl(null);
    setError(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('title')}</DialogTitle>
          <DialogDescription>
            {t('description')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary mb-2" />
              <p className="text-sm text-muted-foreground">
                {t('preparing')}
              </p>
            </div>
          ) : authUrl ? (
            <div className="space-y-4">
              {/* Permissions Notice */}
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                <p className="text-xs text-blue-900">
                  {t.rich('permissions', { strong: (chunks) => <strong>{chunks}</strong> })}
                </p>
              </div>

              {/* Connection Instructions */}
              <div className="rounded-lg border p-4 space-y-3">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <div className="space-y-1">
                    <p className="text-sm font-medium">{t('connectTitle')}</p>
                    <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                      <li>{t('steps.proceed')}</li>
                      <li>{t('steps.signIn')}</li>
                      <li>{t('steps.consent')}</li>
                      <li>{t('steps.company')}</li>
                    </ol>
                  </div>
                </div>
              </div>

              <Button
                onClick={handleConnectRedirect}
                className="w-full"
                size="lg"
              >
                <ExternalLink className="mr-2 h-4 w-4" />
                {t('connect')}
              </Button>

              <p className="text-xs text-center text-muted-foreground">
                {t('redirectNote')}
              </p>
            </div>
          ) : null}

          <div className="flex justify-end pt-2">
            <Button onClick={handleClose} variant="outline">
              {t('cancel')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ErpConnectionModal;
